import { useCallback, useEffect, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { StationAgro } from '../../../domain/WeatherStation';

const POLL_INTERVAL_MS = 10 * 60 * 1000;
const DEFAULT_DAYS = 30;

export interface AgroSettings {
  since: string;
  baseTemp: number;
}

// Hasta que existan los lotes (con su fecha de siembra y cultivo), desde
// cuándo sumar grados día y con qué temperatura base se recuerda por
// estación en este navegador.
const storageKey = (deviceId: string) => `domiot.agro.${deviceId}`;

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function defaultSettings(): AgroSettings {
  const d = new Date();
  d.setDate(d.getDate() - (DEFAULT_DAYS - 1));
  const since = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { since, baseTemp: 0 };
}

function readSettings(deviceId: string): AgroSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey(deviceId)) ?? 'null');
    if (raw && typeof raw.since === 'string' && typeof raw.baseTemp === 'number') return raw;
  } catch {
    // sin storage (modo privado): valen los de referencia
  }
  return defaultSettings();
}

export function useStationAgro(deviceId: string) {
  const { weatherStations } = useRepositories();
  const [settings, setSettingsState] = useState<AgroSettings>(() => readSettings(deviceId));
  const [agro, setAgro] = useState<StationAgro | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setSettings = (next: AgroSettings) => {
    setSettingsState(next);
    try {
      localStorage.setItem(storageKey(deviceId), JSON.stringify(next));
    } catch {
      // no se recuerda, pero sigue andando
    }
  };

  const load = useCallback(() => {
    let cancelled = false;
    weatherStations
      .getAgro(deviceId, settings.since, settings.baseTemp)
      .then(({ agro: data }) => {
        if (cancelled) return;
        setAgro(data);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'No se pudieron calcular los indicadores.');
      });
    return () => {
      cancelled = true;
    };
  }, [weatherStations, deviceId, settings.since, settings.baseTemp]);

  useEffect(() => {
    let cancel = load();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        cancel();
        cancel = load();
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancel();
      clearInterval(interval);
    };
  }, [load]);

  return { agro, error, settings, setSettings, today: localToday() };
}
